// G:\qkard\frontend\src\pages\LookbookPage.jsx
// Phase 1D — Public Lookbook. No auth required.
// URL: qkard.app/look/:slug
//
// Features:
//   - Photo-first grid (2col mobile / 3col tablet / 4col desktop)
//   - Category filter pills
//   - Item detail drawer: photo swipe, variants, specs
//   - Wishlist (sessionStorage, no auth)
//   - Sticky bottom bar: count + WhatsApp share + Show Owner
//   - toCdn() on all photo URLs

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toCdn } from "../config/cdn";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

// ── Wishlist helpers (sessionStorage, cleared when tab closes) ───────────
const WL_KEY = (slug) => `qkard_wishlist_${slug}`;
const loadWishlist = (slug) => {
  try { return JSON.parse(sessionStorage.getItem(WL_KEY(slug)) || "[]"); }
  catch { return []; }
};
const saveWishlist = (slug, items) => {
  try { sessionStorage.setItem(WL_KEY(slug), JSON.stringify(items)); }
  catch {}
};

// ── Rupee formatter ──────────────────────────────────────────────────────
const inr = (n) =>
  "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function LookbookPage() {
  const { slug } = useParams();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch]       = useState("");
  const [selectedItem, setSelectedItem]     = useState(null);
  const [photoIndex, setPhotoIndex]         = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [wishlist, setWishlist]   = useState([]);
  const [showWishlist, setShowWishlist]     = useState(false);
  const [toastMsg, setToastMsg]   = useState("");
  const [wishlistId, setWishlistId] = useState(null);
  const [refCode, setRefCode]       = useState(null);
  const [sessionId]                 = useState(() => {
    let sid = sessionStorage.getItem("qkard_session");
    if (!sid) { sid = crypto.randomUUID(); sessionStorage.setItem("qkard_session", sid); }
    return sid;
  });
  const touchStartX = useRef(null);

  // ── Fetch catalogue ────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API}/catalogue/${slug}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setWishlist(loadWishlist(slug));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  // ── Categories from items ──────────────────────────────────────────────
  const categories = data
    ? ["All", ...Array.from(new Set((data.items || []).map(i => i.category).filter(Boolean))).sort()]
    : [];

  // ── Filtered items ─────────────────────────────────────────────────────
  const filtered = (data?.items || []).filter(item => {
    const catMatch = activeCategory === "All" || item.category === activeCategory;
    const q = search.toLowerCase();
    const searchMatch = !q ||
      item.name?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.tags?.some(t => t.toLowerCase().includes(q));
    return catMatch && searchMatch && item.isActive !== false;
  });

  // ── Wishlist operations ────────────────────────────────────────────────
  const isWishlisted = useCallback(
    (itemId) => wishlist.some(w => w.itemId === itemId),
    [wishlist]
  );

  const persistWishlist = useCallback(async (items, existingWishlistId, existingRefCode) => {
    if (!data?.business?.businessId || items.length === 0) return;
    try {
      const res = await fetch("https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod/wishlists", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId: data.business.businessId,
          sessionId,
          wishlistId: existingWishlistId || undefined,
          referenceCode: existingRefCode || undefined,
          items,
        }),
      });
      const d = await res.json();
      if (d.wishlistId) setWishlistId(d.wishlistId);
      if (d.referenceCode) setRefCode(d.referenceCode);
    } catch (e) {
      console.error("Wishlist persist failed", e);
    }
  }, [data, sessionId]);

  const toggleWishlist = useCallback((item, variantLabel) => {
    setWishlist(prev => {
      const exists = prev.some(w => w.itemId === item.itemId);
      let next;
      if (exists) {
        next = prev.filter(w => w.itemId !== item.itemId);
        showToast("Removed from wishlist");
      } else {
        next = [...prev, {
          itemId: item.itemId,
          name: item.name,
          photo: toCdn(item.photos?.[0] || item.photoUrl),
          price: variantLabel
            ? item.variants?.find(v => v.label === variantLabel)?.price ?? item.price
            : item.price,
          variantSelected: variantLabel || null,
          addedAt: Date.now(),
        }];
        showToast("Saved to wishlist ❤");
      }
      saveWishlist(slug, next);
      // Persist to DynamoDB (fire and forget)
      setTimeout(() => persistWishlist(next, wishlistId, refCode), 0);
      return next;
    });
  }, [slug, wishlistId, refCode, persistWishlist]);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 2000);
  };

  // ── Open item detail ───────────────────────────────────────────────────
  const openItem = (item) => {
    setSelectedItem(item);
    setPhotoIndex(0);
    setSelectedVariant(item.variants?.[0]?.label || null);
  };

  const closeItem = () => setSelectedItem(null);

  // ── Photo swipe touch handlers ─────────────────────────────────────────
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e, photos) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) setPhotoIndex(i => Math.min(i + 1, photos.length - 1));
      else          setPhotoIndex(i => Math.max(i - 1, 0));
    }
    touchStartX.current = null;
  };

  // ── WhatsApp share ────────────────────────────────────────────────────
  const shareOnWhatsApp = () => {
    const lines = wishlist.map(w =>
      `• ${w.name}${w.variantSelected ? ` (${w.variantSelected})` : ""} — ${inr(w.price)}`
    ).join("\n");
    const text = `Hi, I visited *${data?.business?.businessName}* and saved these items:\n\n${lines}\n\nPlease share more details.\n\n*My Wishlist:* ${window.location.href}`;

    // Resolve WhatsApp number — try catalogue first, then business, then phone.
    // Strip all non-digits. wa.me requires country-code prefixed digits only.
    const rawNumber =
      data?.catalogue?.whatsappNumber ||
      data?.business?.whatsapp ||
      data?.business?.whatsappNumber ||
      data?.business?.phone ||
      "";
    const phone = String(rawNumber).replace(/\D/g, "");

    // If we have a number, message goes directly to the boutique.
    // If not, fall back to WhatsApp's contact picker so the share still works.
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  // ── Render states ──────────────────────────────────────────────────────
  if (loading) return <LoadingScreen />;
  if (error)   return <ErrorScreen error={error} />;
  if (!data)   return null;

  const { business } = data;
  const currentItemPhotos = selectedItem
    ? (selectedItem.photos?.length ? selectedItem.photos : [selectedItem.photoUrl]).filter(Boolean).map(toCdn)
    : [];
  const activeVariantPrice = selectedItem
    ? (selectedItem.variants?.find(v => v.label === selectedVariant)?.price ?? selectedItem.price)
    : 0;

  return (
    <div style={s.page}>
      <style>{`
        .qk-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 12px;
        }
        @media (min-width: 600px) {
          .qk-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; padding: 16px; }
        }
        @media (min-width: 960px) {
          .qk-grid { grid-template-columns: repeat(4, 1fr); gap: 20px; padding: 20px; }
        }
        .qk-card { background:#fff; border-radius:12px; overflow:hidden; cursor:pointer;
          box-shadow:0 1px 4px rgba(92,74,53,0.08); transition:transform 0.15s,box-shadow 0.15s; }
        .qk-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(92,74,53,0.13); }
        .qk-photo-wrap { position:relative; width:100%; aspect-ratio:1/1; background:#f0ebe4; overflow:hidden; }
        .qk-photo { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; transition:transform 0.3s; }
        .qk-card:hover .qk-photo { transform:scale(1.04); }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={s.header}>
        <div style={s.headerInner}>
          <div>
            <div style={s.shopName}>{business.businessName}</div>
            <div style={s.shopMeta}>{business.city} · {business.category}</div>
          </div>
          <div style={s.headerRight}>
            <div style={s.qkardBadge}>QKARD</div>
          </div>
        </div>

        {/* Category pills */}
        <div style={s.pillsRow}>
          {categories.map(cat => (
            <button
              key={cat}
              style={{ ...s.pill, ...(activeCategory === cat ? s.pillActive : {}) }}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={s.searchWrap}>
          <span style={s.searchIcon}>🔍</span>
          <input
            style={s.searchInput}
            placeholder="Search designs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button style={s.searchClear} onClick={() => setSearch("")}>✕</button>
          )}
        </div>
      </header>

      {/* ── Grid ───────────────────────────────────────────────────────── */}
      <main className="qk-grid">
        {filtered.length === 0 && (
          <div style={s.empty}>No items found{search ? ` for "${search}"` : ""}</div>
        )}
        {filtered.map(item => (
          <div
            key={item.itemId}
            className="qk-card"
            onClick={() => openItem(item)}
          >
            <div className="qk-photo-wrap">
              <img
                src={toCdn(item.photos?.[0] || item.photoUrl) || ""}
                alt={item.name}
                className="qk-photo"
                loading="lazy"
              />
              {item.photos?.length > 1 && (
                <div style={s.multiPhotoBadge}>+{item.photos.length - 1}</div>
              )}
              <button
                style={{
                  ...s.heartBtn,
                  ...(isWishlisted(item.itemId) ? s.heartBtnActive : {}),
                }}
                onClick={e => { e.stopPropagation(); toggleWishlist(item, null); }}
              >
                {isWishlisted(item.itemId) ? "❤" : "♡"}
              </button>
            </div>
            <div style={s.cardBody}>
              <div style={s.cardName}>{item.name}</div>
              <div style={s.cardPrice}>
                {item.variants?.length
                  ? `${inr(Math.min(...item.variants.map(v => v.price)))} onwards`
                  : inr(item.price)
                }
              </div>
              {item.category && <div style={s.cardTag}>{item.category}</div>}
            </div>
          </div>
        ))}
      </main>

      {/* ── Sticky bottom bar ──────────────────────────────────────────── */}
      {wishlist.length > 0 && !selectedItem && !showWishlist && (
        <div style={s.stickyBar}>
          <button style={s.stickyCount} onClick={() => setShowWishlist(true)}>
            ❤ {wishlist.length} saved{refCode ? ` · ${refCode}` : ""}
          </button>
          <button style={s.stickyShare} onClick={shareOnWhatsApp}>
            📲 Share
          </button>
          <button style={s.stickyOwner} onClick={() => setShowWishlist(true)}>
            Show Owner
          </button>
        </div>
      )}

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toastMsg && <div style={s.toast}>{toastMsg}</div>}

      {/* ── Item Detail Drawer ─────────────────────────────────────────── */}
      {selectedItem && (
        <div style={s.drawerOverlay} onClick={closeItem}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>

            {/* Photo carousel */}
            <div
              style={s.drawerPhotoWrap}
              onTouchStart={onTouchStart}
              onTouchEnd={e => onTouchEnd(e, currentItemPhotos)}
            >
              <img
                src={currentItemPhotos[photoIndex] || ""}
                alt={selectedItem.name}
                style={s.drawerPhoto}
              />
              {/* Prev/Next arrows for desktop */}
              {currentItemPhotos.length > 1 && (
                <>
                  <button
                    style={{ ...s.photoNav, left: 12 }}
                    onClick={() => setPhotoIndex(i => Math.max(i - 1, 0))}
                    disabled={photoIndex === 0}
                  >‹</button>
                  <button
                    style={{ ...s.photoNav, right: 12 }}
                    onClick={() => setPhotoIndex(i => Math.min(i + 1, currentItemPhotos.length - 1))}
                    disabled={photoIndex === currentItemPhotos.length - 1}
                  >›</button>
                </>
              )}
              {/* Photo dots */}
              {currentItemPhotos.length > 1 && (
                <div style={s.photoDots}>
                  {currentItemPhotos.map((_, i) => (
                    <div
                      key={i}
                      style={{ ...s.photoDot, ...(i === photoIndex ? s.photoDotActive : {}) }}
                      onClick={() => setPhotoIndex(i)}
                    />
                  ))}
                </div>
              )}
              <button style={s.closeBtn} onClick={closeItem}>✕</button>
            </div>

            {/* Detail content */}
            <div style={s.drawerContent}>
              <div style={s.drawerName}>{selectedItem.name}</div>
              {selectedItem.description && (
                <div style={s.drawerDesc}>{selectedItem.description}</div>
              )}

              {/* Variants */}
              {selectedItem.variants?.length > 0 && (
                <div style={s.section}>
                  <div style={s.sectionLabel}>Choose option</div>
                  <div style={s.variantsRow}>
                    {selectedItem.variants.map(v => (
                      <button
                        key={v.label}
                        style={{
                          ...s.variantBtn,
                          ...(selectedVariant === v.label ? s.variantBtnActive : {}),
                          ...(v.inStock === false ? s.variantBtnOos : {}),
                        }}
                        onClick={() => v.inStock !== false && setSelectedVariant(v.label)}
                        disabled={v.inStock === false}
                      >
                        <div style={s.variantLabel}>{v.label}</div>
                        <div style={s.variantPrice}>{inr(v.price)}</div>
                        {v.inStock === false && <div style={s.variantOos}>Out of stock</div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Specs */}
              {selectedItem.specs && Object.keys(selectedItem.specs).length > 0 && (
                <div style={s.section}>
                  <div style={s.sectionLabel}>Specifications</div>
                  <div style={s.specsGrid}>
                    {Object.entries(selectedItem.specs).map(([k, v]) => (
                      <div key={k} style={s.specRow}>
                        <span style={s.specKey}>{specLabel(k)}</span>
                        <span style={s.specVal}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Price + CTA */}
              <div style={s.drawerFooter}>
                <div style={s.drawerPrice}>{inr(activeVariantPrice)}</div>
                <button
                  style={{
                    ...s.saveBtn,
                    ...(isWishlisted(selectedItem.itemId) ? s.saveBtnSaved : {}),
                  }}
                  onClick={() => toggleWishlist(selectedItem, selectedVariant)}
                >
                  {isWishlisted(selectedItem.itemId) ? "❤ Saved" : "♡ Save to Wishlist"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Wishlist Panel ─────────────────────────────────────────────── */}
      {showWishlist && (
        <div style={s.drawerOverlay} onClick={() => setShowWishlist(false)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <div style={s.drawerContent}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                <div style={s.drawerName}>My Saved Items ({wishlist.length})</div>
                {refCode && (
                  <div style={{ fontSize: 13, color: "#9e8a75", marginTop: 2 }}>
                    Reference code: <span style={{ fontWeight: 800, fontFamily: "monospace", color: "#2c1f0e", letterSpacing: "1px" }}>{refCode}</span>
                  </div>
                )}
              </div>
                <button style={s.closeBtn2} onClick={() => setShowWishlist(false)}>✕</button>
              </div>

              {wishlist.length === 0 && (
                <div style={s.empty}>No items saved yet. Browse and tap ♡ to save.</div>
              )}

              {wishlist.map(w => (
                <div key={w.itemId} style={s.wishlistRow}>
                  <img src={w.photo} alt={w.name} style={s.wishlistPhoto} />
                  <div style={s.wishlistInfo}>
                    <div style={s.wishlistName}>{w.name}</div>
                    {w.variantSelected && (
                      <div style={s.wishlistVariant}>{w.variantSelected}</div>
                    )}
                    <div style={s.wishlistPrice}>{inr(w.price)}</div>
                  </div>
                  <button
                    style={s.wishlistRemove}
                    onClick={() => toggleWishlist({ itemId: w.itemId }, null)}
                  >✕</button>
                </div>
              ))}

              {wishlist.length > 0 && (
                <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
                  <button style={s.whatsappBtn} onClick={shareOnWhatsApp}>
                    📲 Share on WhatsApp
                  </button>
                  <button style={s.browseBtn} onClick={() => setShowWishlist(false)}>
                    ← Continue Browsing
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Spec key → human label ───────────────────────────────────────────────
function specLabel(key) {
  const map = {
    dimensions: "📐 Size",
    material:   "🏭 Material",
    finish:     "✨ Finish",
    minOrder:   "📦 Min Order",
    weight:     "⚖ Weight",
    leadTime:   "🗓 Lead Time",
    sqftPrice:  "💰 Per Sqft",
    metal:      "🔩 Metal",
    stone:      "💎 Stone",
    venue:      "📍 Venue",
  };
  return map[key] || key;
}

// ── Loading / Error screens ──────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#faf8f5" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🛋</div>
        <div style={{ fontFamily: "Georgia, serif", color: "#5c4a35", fontSize: 16 }}>Loading collection...</div>
      </div>
    </div>
  );
}
function ErrorScreen({ error }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#faf8f5" }}>
      <div style={{ textAlign: "center", padding: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🪑</div>
        <div style={{ fontFamily: "Georgia, serif", color: "#5c4a35", fontSize: 20, marginBottom: 8 }}>Shop not found</div>
        <div style={{ color: "#9e8a75", fontSize: 14 }}>{error}</div>
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: "100vh",
    background: "#faf8f5",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    paddingBottom: 100,
  },

  // Header
  header: {
    background: "#fff",
    borderBottom: "1px solid #ede8e1",
    padding: "16px 16px 0",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 2px 12px rgba(92,74,53,0.06)",
  },
  headerInner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  shopName: {
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 22,
    fontWeight: 700,
    color: "#2c1f0e",
    letterSpacing: "-0.3px",
  },
  shopMeta: {
    fontSize: 13,
    color: "#9e8a75",
    marginTop: 2,
    textTransform: "capitalize",
  },
  headerRight: { display: "flex", alignItems: "center", gap: 8 },
  qkardBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "1.5px",
    color: "#c8a882",
    border: "1px solid #c8a882",
    padding: "3px 7px",
    borderRadius: 4,
  },

  // Pills
  pillsRow: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 12,
    scrollbarWidth: "none",
    WebkitOverflowScrolling: "touch",
  },
  pill: {
    flexShrink: 0,
    padding: "6px 14px",
    borderRadius: 20,
    border: "1.5px solid #ddd5c8",
    background: "transparent",
    color: "#7a6652",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  pillActive: {
    background: "#2c1f0e",
    borderColor: "#2c1f0e",
    color: "#fff",
  },

  // Search
  searchWrap: {
    display: "flex",
    alignItems: "center",
    background: "#f5f0ea",
    borderRadius: 10,
    padding: "8px 12px",
    marginBottom: 12,
    gap: 8,
  },
  searchIcon: { fontSize: 14, opacity: 0.5 },
  searchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontSize: 14,
    color: "#2c1f0e",
    outline: "none",
    fontFamily: "inherit",
  },
  searchClear: {
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 14,
    color: "#9e8a75",
    padding: 0,
  },

  // Grid — CSS grid with auto-fill
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
    padding: 12,
    // 3 col on tablet+, 4 col on wide
    // done via maxWidth tricks since we can't use @media in inline styles
    // The breakpoints are handled by the card's natural reflow
  },

  // Card
  card: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 1px 4px rgba(92,74,53,0.08)",
    transition: "transform 0.15s, box-shadow 0.15s",
  },
  cardPhotoWrap: {
    position: "relative",
    paddingTop: "100%",   // 1:1 square
    background: "#f0ebe4",
    overflow: "hidden",
  },
  cardPhoto: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.3s",
  },
  multiPhotoBadge: {
    position: "absolute",
    bottom: 8,
    left: 8,
    background: "rgba(44,31,14,0.65)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 7px",
    borderRadius: 10,
  },
  heartBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.9)",
    fontSize: 16,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
    transition: "transform 0.15s",
  },
  heartBtnActive: {
    background: "#fff0f0",
    color: "#e03535",
  },
  cardBody: { padding: "10px 12px 12px" },
  cardName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#2c1f0e",
    lineHeight: 1.35,
    marginBottom: 4,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardPrice: {
    fontSize: 13,
    fontWeight: 700,
    color: "#b8722a",
    marginBottom: 4,
  },
  cardTag: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 600,
    color: "#9e8a75",
    background: "#f5f0ea",
    padding: "2px 8px",
    borderRadius: 6,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  // Sticky bar
  stickyBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#2c1f0e",
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    gap: 10,
    zIndex: 200,
    boxShadow: "0 -4px 20px rgba(44,31,14,0.2)",
  },
  stickyCount: {
    flex: 1,
    background: "none",
    border: "none",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
  },
  stickyShare: {
    background: "#25D366",
    border: "none",
    color: "#fff",
    fontWeight: 700,
    fontSize: 13,
    padding: "9px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  stickyOwner: {
    background: "rgba(255,255,255,0.12)",
    border: "1.5px solid rgba(255,255,255,0.3)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    padding: "9px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  // Toast
  toast: {
    position: "fixed",
    bottom: 80,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(44,31,14,0.9)",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 500,
    zIndex: 300,
    whiteSpace: "nowrap",
    boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
  },

  // Drawer overlay + panel
  drawerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(44,31,14,0.5)",
    zIndex: 400,
    display: "flex",
    alignItems: "flex-end",
    backdropFilter: "blur(2px)",
  },
  drawer: {
    background: "#fff",
    width: "100%",
    maxHeight: "92vh",
    borderRadius: "20px 20px 0 0",
    overflowY: "auto",
    maxWidth: 640,
    margin: "0 auto",
  },

  // Item detail drawer
  drawerPhotoWrap: {
    position: "relative",
    width: "100%",
    paddingTop: "66%",
    background: "#f0ebe4",
    overflow: "hidden",
  },
  drawerPhoto: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoNav: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.85)",
    border: "none",
    borderRadius: "50%",
    width: 36,
    height: 36,
    fontSize: 20,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2c1f0e",
    zIndex: 2,
  },
  photoDots: {
    position: "absolute",
    bottom: 10,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: 6,
  },
  photoDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.5)",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  photoDotActive: { background: "#fff" },
  closeBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "rgba(255,255,255,0.9)",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    fontSize: 14,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2c1f0e",
    fontWeight: 700,
    zIndex: 3,
  },
  closeBtn2: {
    background: "none",
    border: "none",
    fontSize: 18,
    cursor: "pointer",
    color: "#9e8a75",
    padding: 4,
  },
  drawerContent: { padding: "20px 20px 32px" },
  drawerName: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    fontWeight: 700,
    color: "#2c1f0e",
    marginBottom: 8,
    lineHeight: 1.3,
  },
  drawerDesc: {
    fontSize: 14,
    color: "#7a6652",
    lineHeight: 1.6,
    marginBottom: 16,
  },

  // Section
  section: { marginBottom: 20 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#9e8a75",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginBottom: 10,
  },

  // Variants
  variantsRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  variantBtn: {
    border: "1.5px solid #ddd5c8",
    borderRadius: 10,
    padding: "8px 14px",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  variantBtnActive: {
    borderColor: "#2c1f0e",
    background: "#faf7f3",
  },
  variantBtnOos: { opacity: 0.45, cursor: "not-allowed" },
  variantLabel: { fontSize: 13, fontWeight: 600, color: "#2c1f0e" },
  variantPrice: { fontSize: 12, color: "#b8722a", fontWeight: 700, marginTop: 2 },
  variantOos: { fontSize: 10, color: "#e03535", marginTop: 2 },

  // Specs
  specsGrid: {
    background: "#faf8f5",
    borderRadius: 10,
    overflow: "hidden",
  },
  specRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderBottom: "1px solid #ede8e1",
    gap: 12,
  },
  specKey: { fontSize: 13, color: "#7a6652", flexShrink: 0 },
  specVal: { fontSize: 13, color: "#2c1f0e", fontWeight: 500, textAlign: "right" },

  // Drawer footer
  drawerFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid #ede8e1",
    gap: 12,
  },
  drawerPrice: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    fontWeight: 700,
    color: "#b8722a",
  },
  saveBtn: {
    flex: 1,
    padding: "13px 20px",
    borderRadius: 10,
    border: "2px solid #2c1f0e",
    background: "#2c1f0e",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.2s",
  },
  saveBtnSaved: {
    background: "#fff0f0",
    borderColor: "#e03535",
    color: "#e03535",
  },

  // Wishlist panel
  wishlistRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "12px 0",
    borderBottom: "1px solid #ede8e1",
  },
  wishlistPhoto: {
    width: 64,
    height: 64,
    borderRadius: 8,
    objectFit: "cover",
    background: "#f0ebe4",
    flexShrink: 0,
  },
  wishlistInfo: { flex: 1, minWidth: 0 },
  wishlistName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#2c1f0e",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  wishlistVariant: { fontSize: 12, color: "#9e8a75", marginTop: 2 },
  wishlistPrice: { fontSize: 14, fontWeight: 700, color: "#b8722a", marginTop: 4 },
  wishlistRemove: {
    background: "none",
    border: "none",
    color: "#ccc",
    fontSize: 16,
    cursor: "pointer",
    padding: 4,
    flexShrink: 0,
  },

  whatsappBtn: {
    width: "100%",
    padding: "14px",
    borderRadius: 10,
    border: "none",
    background: "#25D366",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  browseBtn: {
    width: "100%",
    padding: "13px",
    borderRadius: 10,
    border: "1.5px solid #ddd5c8",
    background: "transparent",
    color: "#7a6652",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  empty: {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: "60px 20px",
    color: "#9e8a75",
    fontSize: 15,
  },
};
